import Image from "next/image"
import Link from "next/link"
import { useState } from "react"
import type { PublicPage } from "../content/content-types.ts"
import { stateLabel } from "../lib/state-copy.ts"
import type { UserView } from "../lib/user-view.ts"
import { AuthenticatedContentDialog, AuthenticatedContentDialogTrigger } from "./authenticated-content-dialog.tsx"
import { FormattedPointAmount } from "./point-amount.tsx"
import { ConsumerProductCard } from "./consumer-product-card.tsx"
import { ConsumerTabLink } from "./consumer-tab-link.tsx"
import { HistoryDetailSheet, type HistoryEntry, historyDisplayLabel, userVisibleHistoryEntries } from "./history-detail-sheet.tsx"

type HomeViewModel = Extract<
  UserView,
  {
    readonly kind: "home"
  }
>
const dateFormatter = new Intl.DateTimeFormat("ko-KR", { month: "short", day: "numeric" })
const HomeChevron = () => <Image unoptimized alt="" aria-hidden="true" className="redesign-home-chevron" height={16} src="/assets/cashhub/redesign/home-chevron-right.svg" width={16} />
const FeaturedRail = ({ products }: { readonly products: NonNullable<HomeViewModel["featuredProducts"]> }) => {
  if (products.length === 0) return null
  const visibleProducts = products.slice(0, 4)
  return (
    <section aria-label="이번 주 인기" className="home-featured">
      <header className="home-section-head">
        <h2>이번 주 인기</h2>
        <ConsumerTabLink href="/shop">
          전체 보기
          <HomeChevron />
        </ConsumerTabLink>
      </header>
      <div className="home-featured-rail">
        {visibleProducts.map((product) => (
          <ConsumerProductCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  )
}
export const HomeView = ({ guidePage, view }: { readonly guidePage: PublicPage; readonly view: HomeViewModel }) => {
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null)
  const available = view.details.find(({ label }) => label === "사용 가능")
  const visibleEntries = userVisibleHistoryEntries(view.entries)
  const expiring = view.details.find(({ label }) => label === "30일 내 소멸")
  const hasExpiringBalance = expiring !== undefined && Number(expiring.value.replace(/[^0-9]/gu, "")) > 0
  return (
    <AuthenticatedContentDialog page={guidePage}>
      <div className="home-layout redesign-home-layout" data-commerce="true" data-home-design="redesign">
        {available === undefined ? null : (
          <section aria-label="잔액 요약" className="redesign-home-balance">
            <div className="redesign-home-balance-heading">
              <p>사용 가능한 통합 잔액</p>
              <Link className="redesign-home-balance-detail" href="/balance">
                {hasExpiringBalance ? (
                  <span className="redesign-home-expiring">
                    <Image unoptimized alt="" aria-hidden="true" height={8} src="/assets/cashhub/redesign/home-expiring.svg" width={8} />
                    소멸 임박
                  </span>
                ) : null}
                <span>잔액 상세 ›</span>
              </Link>
            </div>
            <strong className="redesign-home-balance-amount">
              <FormattedPointAmount value={available.value} />
            </strong>
            <AuthenticatedContentDialogTrigger className="redesign-home-import">포인트 가져오기</AuthenticatedContentDialogTrigger>
          </section>
        )}
        <FeaturedRail products={view.featuredProducts} />
        <section aria-label="포인트 전환 안내" className="redesign-home-intro">
          <header className="home-section-head">
            <h2>포인트를 전환해서 사용해 보세요</h2>
            <AuthenticatedContentDialogTrigger className="redesign-home-text-link">
              전환 방법 보기
              <HomeChevron />
            </AuthenticatedContentDialogTrigger>
          </header>
          <div className="redesign-home-intro-card">
            <span aria-hidden="true" className="redesign-home-gift">
              🎁
            </span>
            <div>
              <h3>인기 상품권으로 바꾸기</h3>
              <p>
                이용 중인 제휴 서비스에서 포인트를
                <br />
                전환하면 캐시허브 통합잔액으로 확인됩니다.
              </p>
            </div>
          </div>
        </section>
        <section className="recent-activity redesign-home-activity">
          <header className="home-section-head">
            <h2>최근 이용 내역</h2>
            <Link href="/balance/history">
              내역 보기
              <HomeChevron />
            </Link>
          </header>
          {visibleEntries.length === 0 ? (
            <p className="recent-empty">전환하거나 사용한 내역이 생기면 여기에 보여드릴게요.</p>
          ) : (
            <ul className="activity-list">
              {visibleEntries.slice(0, 5).map((entry) => (
                <li key={entry.id}>
                  <button className="activity-row-button" onClick={() => setSelectedEntry(entry)} type="button">
                    <span>
                      <strong>{historyDisplayLabel(entry.label, entry.sourceMediaName)}</strong>
                      <small>
                        <time dateTime={entry.occurredAt}>{dateFormatter.format(new Date(entry.occurredAt))}</time>
                        <span aria-hidden="true">·</span>
                        <span>{stateLabel(entry.status)}</span>
                      </small>
                    </span>
                    <b data-amount-direction={entry.amount.trim().startsWith("-") ? "debit" : "credit"}>
                      <FormattedPointAmount value={entry.amount} />
                    </b>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <HistoryDetailSheet entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      </div>
    </AuthenticatedContentDialog>
  )
}
